/**
 * S-04 月間一覧。日別の入力・提出・承認状況を確認する読み取り専用の一覧。
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { endpoints } from '../../api/endpoints.js';
import { MonthNav } from '../../components/MonthNav.js';
import { Card, ErrorMessage, Loading, Notice, StatusPill } from '../../components/ui.js';
import { currentMonth, formatDuration, formatTimeOrDash } from '../../lib/format.js';
import { useAsync } from '../../lib/useAsync.js';

export function MonthlyListPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [month, setMonth] = useState(currentMonth());
  const state = useAsync(() => endpoints.monthlyReports(month), [month]);

  return (
    <>
      <MonthNav
        month={month}
        onChange={setMonth}
        right={
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => navigate('/monthly-entry')}
          >
            月間一覧入力へ
          </button>
        }
      />

      {state.error !== null && <ErrorMessage error={state.error} />}
      {state.loading && !state.data && <Loading />}

      {state.data && (
        <>
          {state.data.closed && (
            <Notice tone="info" title="この月は確定済みです">
              内容は編集できません。
            </Notice>
          )}

          <Card title="日別の状況" description="行をクリックすると1日ずつの入力画面へ移動します。">
            <div className="table-wrap">
              <table className="table table--hover">
                <thead>
                  <tr>
                    <th>日付</th>
                    <th>日種別</th>
                    <th>勤務種別</th>
                    <th>始業</th>
                    <th>終業</th>
                    <th className="text-right">実働</th>
                    <th className="text-right">移動</th>
                    <th className="text-right">稼働</th>
                    <th className="text-right">日残業</th>
                    <th>状態</th>
                  </tr>
                </thead>
                <tbody>
                  {state.data.days.map((day) => (
                    <tr
                      key={day.date}
                      className="cursor-pointer"
                      onClick={() => navigate(`/daily/${day.date}`)}
                    >
                      <td className="font-semibold whitespace-nowrap">
                        {Number(day.date.slice(8))}日（{day.weekday}）
                      </td>
                      <td>
                        <span
                          className={`pill ${day.dayType === '平日' ? 'pill--muted' : 'pill--info'}`}
                        >
                          {day.dayType}
                        </span>
                      </td>
                      <td>{day.report?.workType ?? '—'}</td>
                      <td>{formatTimeOrDash(day.report?.workStart)}</td>
                      <td>{formatTimeOrDash(day.report?.workEnd)}</td>
                      <td className="text-right">
                        {day.computed ? formatDuration(day.computed.workMinutes) : '—'}
                      </td>
                      <td className="text-right">
                        {day.computed ? formatDuration(day.computed.travelMinutes) : '—'}
                      </td>
                      <td className="text-right">
                        {day.computed ? formatDuration(day.computed.totalMinutes) : '—'}
                      </td>
                      <td className="text-right">
                        {day.computed && day.computed.dailyOvertimeMinutes > 0
                          ? formatDuration(day.computed.dailyOvertimeMinutes)
                          : '—'}
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <StatusPill
                            status={day.report?.status ?? null}
                            locked={day.report?.locked}
                          />
                          {day.issues.length > 0 && (
                            <span
                              className={`pill ${
                                day.issues.some((issue) => issue.severity === 'error')
                                  ? 'pill--danger'
                                  : 'pill--warn'
                              }`}
                            >
                              {day.issues.length}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </>
  );
}
